import { useFeatureFlags } from "@/core/auth/hooks";
import { tKey } from "@/core/i18n";
import {
  useAddReplayVideoFromUrlMutation,
  useAddReplayVideoMutation,
  useCreateDashboardDeploymentMutation,
  useDeployDashboardMutation,
  useGetDashboardQuery,
  useGetNNQuery,
  useLazyGetDashboardDeploymentQuery,
  useRemoveDashboardMutation,
  useRemoveReplayVideoMutation,
} from "@/core/cameraApi";
import type { DeviceInfo } from "@/core/cameraApi/schemas";
import type {
  DeployConfigEntry,
  DeploymentStatus,
} from "@/core/cameraApi/schemas/dashboard";
import {
  deploymentProgress,
  isDeploymentTerminal,
} from "@/core/cameraApi/schemas/dashboard";
import type { NNRuntimeConfig, SahiConfig } from "@/core/cameraApi/schemas/nn";
import { DEFAULT_NN_RUNTIME_CONFIG } from "@/core/cameraApi/schemas/nn";
import {
  useLazyGetDashboardConfigsQuery,
  useLazyGetDeployPayloadQuery,
} from "@/modules/dashboard/services/dashboardConfigApi";

import { cameraApi } from "@/core/cameraApi";
import { CameraApiTagType } from "@/core/cameraApi/tagType";
import { useAppDispatch } from "@/hooks/redux";
import { bumpPipeline } from "@/modules/camera-stream/services/cameraPipelineGenerationSlice";
import { useLazyGetCapturedVideoQuery } from "@/modules/capture/services/captureApi";
import { useLazyGetModelOutputsQuery } from "@/modules/model/services/modelApi";
import { useLazyGetProjectsQuery } from "@/modules/project/services/projectApi";
import type {
  DashboardConfiguration,
  DashboardDeployPayload,
} from "@repo/schema";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { computeSettingsUnlock } from "../utils/dashboardUnlock";

/**
 * Camera builds without the deployments route answer 404; a body-shape
 * rejection on an intermediate build answers 415. Either means "this camera
 * cannot pull models itself" — upload the bytes the old way instead.
 *
 * Note what is absent: 502. For the replay route that means "the camera could
 * not fetch the URL" and falling back is right. For a deployment it is a real
 * download failure the operator has to see, not something to paper over.
 */
const DEPLOYMENT_FALLBACK_STATUSES = new Set([404, 415]);

/** How often to ask the camera how a deployment is going. */
const DEPLOYMENT_POLL_INTERVAL_MS = 1000;

// Camera versions older than the URL-pull endpoint respond 404/405; if the
// camera is reachable but can't fetch the URL itself it responds 502. Any of
// these mean we should fall back to browser-side buffered upload rather than
// surface the error.
const URL_PULL_FALLBACK_STATUSES = new Set([404, 405, 502]);

/** Shown on the greyed-out Deploy button and if a deploy starts without a model anyway. */
export const NO_MODEL_MESSAGE = tKey("run:deploy.noModel");

export interface ConfigSelection {
  configId: number;
  projectId: number;
}

export type DeployPhase =
  | "idle"
  | "preparing"
  | "loading-data"
  | "downloading-model"
  | "uploading-video"
  | "removing-video"
  | "deploying"
  | "stopping";

/**
 * What the camera is doing, while it is doing it.
 *
 * Only populated on the pull path — the multipart fallback cannot report
 * progress, because the browser is the one moving the bytes and
 * `fetchBaseQuery` exposes no upload progress.
 */
export interface DeployProgress {
  /** 0..1, or null when the artifacts could not be sized. */
  fraction: number | null;
  bytesDownloaded: number;
  bytesTotal: number;
  /** True when every model was already cached and nothing transferred. */
  allCached: boolean;
}

interface UseRunDeployParams {
  selectedCamera: string | null;
  selectedStream: string | null;
  selectedCameraInfo: DeviceInfo | undefined;
  activeConfigId: number | null;
  activeProjectId: number | null;
  /** Whether the active configuration has a model, counting an unsaved pick. */
  hasModel: boolean;
  capturedVideoId: number | null;
  cameraApiUrl: string | null;
  selectedConfigs?: ConfigSelection[];
  sahiConfig: SahiConfig | null;
  runtimeConfig?: NNRuntimeConfig;
  // Returns the freshly-saved deploy-time snapshot for fields that the form
  // owns. We use this for replay video because the prop value comes from RTK
  // Query and stays stale for one tick after saveIfDirty invalidates the tag.
  beforeDeploy?: () => Promise<{ capturedVideoId: number | null } | undefined>;
}

/** One configuration ready to deploy: the camera wire payload, as the API built it. */
interface AssembledConfig {
  payload: DashboardDeployPayload;
  configId: number;
}

export const useRunDeploy = ({
  selectedCamera,
  selectedStream,
  selectedCameraInfo,
  activeConfigId,
  activeProjectId,
  hasModel,
  capturedVideoId,
  cameraApiUrl,
  selectedConfigs,
  sahiConfig,
  runtimeConfig = DEFAULT_NN_RUNTIME_CONFIG,
  beforeDeploy,
}: UseRunDeployParams) => {
  const { t } = useTranslation(["run", "common"]);
  // Payment overdue — nothing in this hook may deploy to the camera. Stopping
  // is still allowed; see handleStop.
  const { restricted } = useFeatureFlags();

  const dispatch = useAppDispatch();

  const [showDeployConfirm, setShowDeployConfirm] = useState(false);
  const [deployPhase, setDeployPhase] = useState<DeployPhase>("idle");
  const [deployProgress, setDeployProgress] = useState<DeployProgress | null>(
    null,
  );
  const [dashboardReloadNonce, setDashboardReloadNonce] = useState(0);

  // Single source of truth: the camera itself. The GET endpoint returns the
  // dashboard HTML (200) or 404 if none is running. No local mirror — that's
  // what caused the "can't stop after refresh" and stale-banner bugs.
  const { isSuccess: isRemoteDashboardDeployed } = useGetDashboardQuery(
    { mxid: selectedCamera!, streamName: selectedStream! },
    { skip: !selectedCamera || !selectedStream },
  );
  const { data: isRemoteModelDeployed } = useGetNNQuery(
    { mxid: selectedCamera!, streamName: selectedStream! },
    { skip: !selectedCamera || !selectedStream },
  );

  const [settingsUnlockToken, setSettingsUnlockToken] = useState<string | null>(
    null,
  );
  useEffect(() => {
    if (!selectedCamera || !selectedStream) {
      setSettingsUnlockToken(null);
      return;
    }
    let cancelled = false;
    computeSettingsUnlock(selectedCamera, selectedStream).then((token) => {
      if (!cancelled) setSettingsUnlockToken(token);
    });
    return () => {
      cancelled = true;
    };
  }, [selectedCamera, selectedStream]);

  // Nulling the URL for a restricted org does double duty: it drops the
  // dashboard iframe *and* guarantees the `s` settings-unlock token is never
  // handed out, so the camera-hosted dashboard can't be reconfigured either.
  const effectiveDashboardUrl =
    isRemoteDashboardDeployed && !restricted
      ? (() => {
          const base = `${cameraApiUrl}/cameras/${selectedCamera}/streams/${selectedStream}/dashboard`;
          const params = new URLSearchParams();
          if (settingsUnlockToken) params.set("s", settingsUnlockToken);
          if (dashboardReloadNonce > 0)
            params.set("_r", String(dashboardReloadNonce));
          const qs = params.toString();
          return qs ? `${base}?${qs}` : base;
        })()
      : null;
  const isDeployed = !!isRemoteModelDeployed;

  // Lazy triggers for multi-config deploy. The eval/label/model fan-out these
  // used to drive now happens server-side behind getDeployPayload.
  const [triggerGetProjects] = useLazyGetProjectsQuery();
  const [triggerGetDashboardConfigs] = useLazyGetDashboardConfigsQuery();
  const [triggerGetDeployPayload] = useLazyGetDeployPayloadQuery();
  const [triggerGetDeployment] = useLazyGetDashboardDeploymentQuery();
  // Only the upload fallback needs artifact URLs.
  const [triggerGetModelOutputs] = useLazyGetModelOutputsQuery();

  // Mutations
  const [createDeploymentMut] = useCreateDashboardDeploymentMutation();
  const [deployDashboardMut] = useDeployDashboardMutation();
  const [removeDashboardMut] = useRemoveDashboardMutation();
  const [addReplayVideoMut] = useAddReplayVideoMutation();
  const [addReplayVideoFromUrlMut] = useAddReplayVideoFromUrlMutation();
  const [removeReplayVideoMut] = useRemoveReplayVideoMutation();
  const [triggerGetCapturedVideo] = useLazyGetCapturedVideoQuery();

  const canDeploy =
    !restricted && !!selectedCamera && !!selectedStream && hasModel;

  /**
   * Refetch everything that reads "is something deployed here".
   *
   * The multipart mutation did this via `invalidatesTags`, but the pull path
   * answers 202 before anything exists on the camera, so invalidating there
   * would refetch too early and cache a not-yet-deployed state. Called instead
   * once the deployment reports completion.
   */
  const invalidateDeployedState = (mxid: string, streamName: string) => {
    dispatch(
      cameraApi.util.invalidateTags([
        { type: CameraApiTagType.NN, id: `${mxid}-${streamName}` },
        { type: CameraApiTagType.Dashboard, id: `${mxid}-${streamName}` },
      ]),
    );
  };

  /** What a caught error says, for the toasts below. */
  const errorText = (error: unknown): string =>
    error instanceof Error ? error.message : t("deploy.unknownError");

  /** Called when user clicks Deploy. Shows confirmation if a dashboard is already running. */
  const handleDeploy = () => {
    if (restricted) return;
    if (!selectedCamera || !selectedStream || !selectedCameraInfo) return;

    if (!hasModel) {
      toast.error(t(NO_MODEL_MESSAGE));
      return;
    }

    if (isDeployed) {
      setShowDeployConfirm(true);
      return;
    }

    executeDeploy();
  };

  /** Actually runs the deploy (called directly or after user confirms override). */
  const executeDeploy = async () => {
    if (restricted) return;
    if (!selectedCamera || !selectedStream || !selectedCameraInfo) return;

    let effectiveCapturedVideoId = capturedVideoId;

    try {
      if (beforeDeploy) {
        setDeployPhase("preparing");
        try {
          const snapshot = await beforeDeploy();
          if (snapshot) {
            effectiveCapturedVideoId = snapshot.capturedVideoId;
          }
        } catch (error) {
          toast.error(t("deploy.saveFailed", { error: errorText(error) }));
          return;
        }
      }

      setDeployPhase("loading-data");

      // Fetch all projects and their dashboard configs
      const allProjects = await triggerGetProjects().unwrap();
      const projectConfigPairs = await Promise.all(
        allProjects.map(async (project) => {
          const projectConfigs = await triggerGetDashboardConfigs({
            projectId: project.id,
          }).unwrap();
          return projectConfigs.map((config) => ({
            projectId: project.id,
            projectName: project.name,
            config,
          }));
        }),
      );

      // Checked against what beforeDeploy just saved, not the form. The filter
      // below drops a configuration without a model silently, so a modelless
      // active configuration used to go missing and every other project's
      // configurations deployed in its place.
      const activeEntry = projectConfigPairs
        .flat()
        .find((e) => e.config.id === activeConfigId);
      if (activeEntry?.config.modelId == null) {
        toast.error(t(NO_MODEL_MESSAGE));
        return;
      }

      let allConfigEntries = projectConfigPairs
        .flat()
        .filter((e) => e.config.modelId != null);

      // If user has selected specific configs, filter to only those + active config
      if (selectedConfigs && selectedConfigs.length > 0) {
        const selectedIds = new Set(selectedConfigs.map((s) => s.configId));
        if (activeConfigId != null) selectedIds.add(activeConfigId);
        allConfigEntries = allConfigEntries.filter((e) =>
          selectedIds.has(e.config.id),
        );
      }

      if (allConfigEntries.length === 0) {
        toast.error(t("deploy.noDeployableConfigs"));
        return;
      }

      // One payload per config, assembled by the API. No model bytes here —
      // the camera fetches those itself from the references in the payload.
      const assembled: AssembledConfig[] = [];
      const skippedReasons: string[] = [];

      await Promise.all(
        allConfigEntries.map(
          async ({ projectId: pid, projectName, config }) => {
            try {
              const result = await fetchDeployPayload(
                pid,
                config,
                selectedCameraInfo.platform,
              );
              if (result) {
                assembled.push(result);
              } else {
                skippedReasons.push(
                  t("deploy.skippedNoOutput", {
                    config: config.name,
                    project: projectName,
                    platform: selectedCameraInfo.platform,
                  }),
                );
              }
            } catch {
              skippedReasons.push(
                t("deploy.skippedFailed", {
                  config: config.name,
                  project: projectName,
                }),
              );
            }
          },
        ),
      );

      if (skippedReasons.length > 0) {
        toast.warning(
          t("deploy.skipped", {
            count: skippedReasons.length,
            reasons: skippedReasons.join(", "),
          }),
        );
      }

      if (assembled.length === 0) {
        toast.error(t("deploy.noneAssembled"));
        return;
      }

      // Active config first — the camera deploys the first one immediately.
      assembled.sort((a, b) => {
        if (a.configId === activeConfigId) return -1;
        if (b.configId === activeConfigId) return 1;
        return 0;
      });

      // Handle replay video before deploying
      setDeployPhase(
        effectiveCapturedVideoId != null && activeProjectId != null
          ? "uploading-video"
          : "removing-video",
      );
      try {
        if (effectiveCapturedVideoId != null && activeProjectId != null) {
          const video = await triggerGetCapturedVideo({
            projectId: activeProjectId,
            videoId: effectiveCapturedVideoId,
          }).unwrap();

          const uploadBuffered = async () => {
            const videoResponse = await fetch(video.fileUrl);
            if (!videoResponse.ok) {
              throw new Error(
                t("deploy.replayDownloadFailed", {
                  status: videoResponse.status,
                }),
              );
            }
            const videoBlob = await videoResponse.blob();
            const videoFile = new File([videoBlob], "replay.webm", {
              type: videoBlob.type || "video/webm",
            });

            await addReplayVideoMut({
              mxid: selectedCamera,
              streamName: selectedStream,
              video: videoFile,
            }).unwrap();
          };

          // Preferred path: camera downloads the video itself. Avoids browser
          // buffering the whole file and sidesteps the HTTP/2 streaming-body
          // requirement for large in-browser uploads.
          try {
            await addReplayVideoFromUrlMut({
              mxid: selectedCamera,
              streamName: selectedStream,
              url: video.fileUrl,
              filename: "replay.webm",
            }).unwrap();
          } catch (err) {
            const status = (err as { status?: unknown })?.status;
            const shouldFallBack =
              typeof status === "number" &&
              URL_PULL_FALLBACK_STATUSES.has(status);
            if (!shouldFallBack) throw err;
            console.warn(
              "Camera URL-pull replay upload unavailable; falling back to buffered browser upload",
              err,
            );
            await uploadBuffered();
          }
        } else {
          await removeReplayVideoMut({
            mxid: selectedCamera,
            streamName: selectedStream,
          })
            .unwrap()
            .catch(() => {});
        }
      } catch (error) {
        toast.error(t("deploy.replayFailed", { error: errorText(error) }));
        return;
      }

      setDeployPhase("deploying");

      // Previously this call was unguarded, so a 413 or 502 surfaced as an
      // unhandled rejection with nothing shown to the operator — the "413
      // looks like a camera fault" case. Both paths now report.
      try {
        const pulled = await deployByReference(
          selectedCamera,
          selectedStream,
          assembled,
        );

        if (!pulled) {
          // Older camera: move the bytes through the browser after all.
          setDeployPhase("downloading-model");
          await deployByUpload(selectedCamera, selectedStream, assembled);
        }
      } catch (error) {
        toast.error(t("deploy.failed", { error: errorText(error) }));
        return;
      }

      // Increment the nonce so the iframe src changes and the browser reloads
      // the frame even when the camera/stream/token are unchanged (redeploy).
      setDashboardReloadNonce((n) => n + 1);
    } finally {
      setDeployPhase("idle");
      setDeployProgress(null);
    }
  };

  /**
   * Fetch the camera payload for one configuration.
   *
   * One call where there used to be 5 + T + sum(L): the API assembles the test
   * case tree, resolves the labels and picks the artifact, and returns the
   * camera's own wire format. A null result means this configuration has no
   * artifact this camera can run.
   */
  async function fetchDeployPayload(
    pid: number,
    config: DashboardConfiguration,
    platform: string,
  ): Promise<AssembledConfig | null> {
    const payload = await triggerGetDeployPayload({
      projectId: pid,
      configId: config.id,
      platform,
    }).unwrap();

    if (!payload) return null;

    // SAHI and the runtime knobs are the one part the API cannot own: SAHI is
    // ephemeral UI state and the thread/throttle values are a client constant.
    // The API returns the camera's defaults; overlay what the operator chose.
    return {
      configId: config.id,
      payload: {
        ...payload,
        nn_config: {
          ...payload.nn_config,
          num_inference_threads: runtimeConfig.num_inference_threads,
          throttle_hz: runtimeConfig.throttle_hz,
          nn_config: sahiConfig ? { sahi_config: sahiConfig } : {},
        },
      },
    };
  }

  /**
   * Watch a deployment to completion.
   *
   * A failed poll is treated as "still going", not as a failure: the camera
   * blocks its event loop while restarting the DepthAI pipeline, so a request
   * issued during the deploy step answers late or times out. Only a 404 is
   * conclusive — the camera has forgotten the deployment, which means it
   * restarted and nothing is coming.
   */
  async function awaitDeployment(
    mxid: string,
    streamName: string,
    deploymentId: string,
  ): Promise<void> {
    for (;;) {
      await new Promise((resolve) =>
        setTimeout(resolve, DEPLOYMENT_POLL_INTERVAL_MS),
      );

      let status: DeploymentStatus;
      try {
        status = await triggerGetDeployment({
          mxid,
          streamName,
          deploymentId,
        }).unwrap();
      } catch (err) {
        if ((err as { status?: unknown })?.status === 404) {
          throw new Error(t("deploy.cameraRestarted"));
        }
        continue;
      }

      const bytesDownloaded = status.models.reduce(
        (sum, model) => sum + model.bytes_downloaded,
        0,
      );
      const bytesTotal = status.models.reduce(
        (sum, model) => sum + (model.size_bytes ?? 0),
        0,
      );

      setDeployProgress({
        fraction: deploymentProgress(status),
        bytesDownloaded,
        bytesTotal,
        allCached:
          status.models.length > 0 &&
          status.models.every((model) => model.cached),
      });

      setDeployPhase(
        status.phase === "deploying" ? "deploying" : "downloading-model",
      );

      if (!isDeploymentTerminal(status.phase)) continue;

      if (status.phase === "failed") {
        throw new Error(status.error ?? t("deploy.failedOnCamera"));
      }

      return;
    }
  }

  /**
   * Deploy by reference, letting the camera fetch the archives itself.
   *
   * @returns false if this camera has no deployments route, so the caller
   *          should fall back to uploading the bytes.
   */
  async function deployByReference(
    mxid: string,
    streamName: string,
    assembled: AssembledConfig[],
  ): Promise<boolean> {
    let accepted: DeploymentStatus;

    try {
      accepted = await createDeploymentMut({
        mxid,
        streamName,
        configs: assembled.map((a) => a.payload),
      }).unwrap();
    } catch (err) {
      const status = (err as { status?: unknown })?.status;
      if (
        typeof status === "number" &&
        DEPLOYMENT_FALLBACK_STATUSES.has(status)
      ) {
        console.warn(
          "Camera has no model-pull deployment route; falling back to uploading the models",
          err,
        );
        return false;
      }
      throw err;
    }

    await awaitDeployment(mxid, streamName, accepted.deployment_id);
    invalidateDeployedState(mxid, streamName);
    dispatch(bumpPipeline({ mxid, streamName }));

    return true;
  }

  /**
   * The pre-pull deploy path, kept for cameras that predate the deployments
   * route: download every archive in the browser and post them all as one
   * multipart body. This is the shape that reached 266 MB and tripped the
   * relay's body limit, so it is a fallback and not a peer.
   */
  async function deployByUpload(
    mxid: string,
    streamName: string,
    assembled: AssembledConfig[],
  ): Promise<void> {
    const configs: DeployConfigEntry[] = [];
    const models: File[] = [];

    for (const { payload } of assembled) {
      // The deploy payload carries a reference, not a location — the camera
      // resolves it against the control plane. Only this fallback needs the
      // artifact URL, so only this fallback asks for it.
      const outputs = await triggerGetModelOutputs({
        projectId: payload.dashboard_config.projectId,
        modelId: payload.nn_config.model_id!,
      }).unwrap();

      const output = outputs.find((o) => o.id === payload.model.output_id);

      if (!output) {
        throw new Error(
          t("deploy.artifactUnavailable", { id: payload.model.output_id }),
        );
      }

      const response = await fetch(output.filePath);

      // The pre-existing version of this had no ok check, so a 404 HTML page
      // could be packaged as model.tar.xz and shipped to the camera.
      if (!response.ok) {
        throw new Error(
          t("deploy.modelDownloadFailed", {
            id: payload.model.output_id,
            status: response.status,
          }),
        );
      }

      models.push(
        new File([await response.arrayBuffer()], payload.model.filename),
      );
      configs.push({
        dashboard_config: payload.dashboard_config,
        nn_config: payload.nn_config,
      });
    }

    await deployDashboardMut({
      mxid,
      streamName,
      configs,
      models,
    }).unwrap();
  }

  const handleStop = async () => {
    if (!selectedCamera || !selectedStream) return;

    setDeployPhase("stopping");
    try {
      await removeDashboardMut({
        mxid: selectedCamera,
        streamName: selectedStream,
      })
        .unwrap()
        .catch(() => {});
      await removeReplayVideoMut({
        mxid: selectedCamera,
        streamName: selectedStream,
      })
        .unwrap()
        .catch(() => {});
      // Tag invalidation in the mutations drops dashboardUrl to null via the
      // query refetch — no local state to reset.
      setDashboardReloadNonce((n) => n + 1);
    } finally {
      setDeployPhase("idle");
    }
  };

  const handleConfirmDeploy = () => {
    setShowDeployConfirm(false);
    executeDeploy();
  };

  const handleCancelDeploy = () => {
    setShowDeployConfirm(false);
  };

  return {
    handleDeploy,
    handleStop,
    isDeploying: deployPhase !== "idle",
    deployPhase,
    deployProgress,
    dashboardUrl: effectiveDashboardUrl,
    canDeploy,
    showDeployConfirm,
    handleConfirmDeploy,
    handleCancelDeploy,
  };
};
